import type {Editor} from "@tiptap/core";
import {useSyncExternalStore} from "react";

/*
    The editor the author focused last, for every chip and menu on the page: one document listener,
    however many editors there are. Focus that lands outside any editor — a side panel, a toolbar
    popover's search field — leaves it as it was.
 */
let lastFocusedEditor: Element | null = null;
const listeners = new Set<() => void>();

const onFocusIn = (event: FocusEvent) => {
    const focused = event.target instanceof Element ? event.target.closest(".ProseMirror") : null;
    if (!focused || focused === lastFocusedEditor) return;

    lastFocusedEditor = focused;
    listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
    if (listeners.size === 0) document.addEventListener("focusin", onFocusIn);
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) document.removeEventListener("focusin", onFocusIn);
    };
};

const lastEditor = () => lastFocusedEditor;

/**
 * True when the author's last editor was a different one.
 *
 * A field keeps its node selection after it blurs, so with two fields on a page (a survey question's
 * title and description) the one left behind would still show its chip selected and its menu open.
 * Only moving into another editor counts: a side panel's Default field or a toolbar popover is still
 * work on the same field, and its selected chip stays visibly selected meanwhile.
 */
export function useFocusInAnotherEditor(editor: Editor): boolean {
    const last = useSyncExternalStore(subscribe, lastEditor, lastEditor);
    // An editor that has since left the page (its question was closed) no longer counts as the last.
    if (!last?.isConnected || editor.isDestroyed) return false;

    return last !== editor.view.dom;
}

export default useFocusInAnotherEditor;
